import React from 'react';
import { useIntl, defineMessages } from 'react-intl';
import HfReactHelper from 'common/utils/hf-react-helper';
import { WordmarkLogo, GlyphLogo, HelloSignWordmark, HelloSignGlyph } from 'dig-components/logos';
import { getBrandName, SiteCodeType } from 'hello-react/common/brand';

// TODO: Get actual HF wordmark and glyphs
import HelloFaxWordmark from './hf-wordmark'; // TEMP
import HelloFaxGlyph from './hf-glyph'; // TEMP

// TOOD: Replace with real Backdrop wordmarks a sprint
// or two before launch.
import BackdropWordmark from './backdrop-wordmark';
import BackdropGlyph from './backdrop-glyph';

import styles from './styles.scss';

const messages = defineMessages({
  altTextWordmark: {
    id: '',
    description: 'Screenreader helper text for the logo which takes users to the home page when pressed.',
    defaultMessage: '{brandName} logotype; Click to go home',
  },
  altTextGlyph: {
    id: '',
    description: 'Screenreader helper text for the HelloFax logo which takes users to the home page when pressed.',
    defaultMessage: '{brandName} logo; Click to go home',
  },
  goHome: {
    id: '',
    description: 'Title text for a button which takes the user home when pressed.',
    defaultMessage: 'Go home',
  },
});

type LogoProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  siteCode: SiteCodeType,
  variant?: 'wordmark' | 'glyph',
  isBackdrop?: boolean,
}

const Logo: React.FC<LogoProps> = ({
  siteCode,
  variant = 'wordmark',
  className = '',
  isBackdrop = false,
}) => {
  const intl = useIntl();
  const isFax = HfReactHelper.HfSites.isHelloFax(siteCode);
  const site = HfReactHelper.HfSites.getSite(siteCode);
  const href = HfReactHelper.urlHelper('home/index', site);

  const wordmarkSrc = React.useMemo(() => {
    if (isBackdrop) return BackdropWordmark;

    return isFax ? HelloFaxWordmark : HelloSignWordmark;
  }, [isFax, isBackdrop]);

  const glyphSrc = React.useMemo(() => {
    if (isBackdrop) return BackdropGlyph;

    return isFax ? HelloFaxGlyph : HelloSignGlyph;
  }, [isFax, isBackdrop]);

  const altText = React.useMemo(() => {
    if (variant === 'wordmark') {
      return intl.formatMessage(messages.altTextGlyph, {
        brandName: getBrandName(siteCode),
      });
    } else {
      return intl.formatMessage(messages.altTextWordmark, {
        brandName: getBrandName(siteCode),
      });
    }
  }, [intl, variant, siteCode]);

  return (
    <div className={className} data-qa-ref="hellosign-app-logo-container">
      <a
        href={href}
        title={intl.formatMessage(messages.goHome)}
        aria-label={altText}
        data-qa-ref="hellosign-app-logo"
      >
        {variant === 'wordmark' ? (
          <WordmarkLogo
            size={32}
            className={styles.logo}
            src={wordmarkSrc} />
        ) : (
          <GlyphLogo
            size={32}
            className={styles.logo}
            src={glyphSrc} />
        )}
      </a>
    </div>
  );
};

export default Logo;
